import React from 'react';
import { view } from '@risingstack/react-easy-state';
import { Modal, Message, Button, Loader, Table, Text } from 'ui';
import { prefixClassName } from 'framework/components/ui/_conf';
import { i18n } from 'i18n';
import { FileManagerHierarchy } from './FileManagerHierarchy';
import { FILEMANAGER_MODAL_MOVE_TO_FOLDER, missingDataWarning } from './constants';
import { useMoveFolderFileManager, MoveFolderFileManagerProps } from './useInternalFileManager';
import { FileFolder } from './types';

const cssClass = prefixClassName('file-manager');

export const isBulkMoveMode = (movableFiles?: FileFolder[]): boolean => !!(movableFiles && movableFiles.length > 0);

export const getFileCount = (isBulkMove: boolean, movableFiles?: FileFolder[]): number =>
  isBulkMove && movableFiles ? movableFiles.length : 1;

export const getModalCaption = (isBulkMove: boolean): string =>
  isBulkMove ? `modules.contentmanagement.selectFolderForFiles` : 'modules.contentmanagement.selectFolder';

export const getMoveButtonCaption = (isBulkMove: boolean): string =>
  isBulkMove ? `modules.contentmanagement.moveFiles` : 'modules.contentmanagement.move';

export const hasMissingData = (missingData?: any): boolean => !!missingData;
export const isStoreInitializing = (store: any): boolean => !!store?.initializing;

export const FileManagerMoveFileModal = view(
  ({ movableFile, movableFiles, onFileMoved }: MoveFolderFileManagerProps) => {
    const { store, missingData, handleRowExpanding, handleHighlightFolder, handleMoveFile } = useMoveFolderFileManager({
      movableFile,
      movableFiles,
      onFileMoved,
    });

    const isBulkMove = isBulkMoveMode(movableFiles);
    const fileCount = getFileCount(isBulkMove, movableFiles);
    const modalCaption = getModalCaption(isBulkMove);

    return (
      <Modal
        closeOnUnmount
        name={FILEMANAGER_MODAL_MOVE_TO_FOLDER}
        caption={modalCaption}
        footerComponent={() => (
          <div className={`${cssClass}__footer`}>
            <div className={`${cssClass}__footer--warning`}>
              {hasMissingData(missingData) ? <Message caption={missingDataWarning[missingData]} type="error" /> : null}
            </div>
            <Button caption={getMoveButtonCaption(isBulkMove)} onClick={handleMoveFile} testId="moveFile" />
          </div>
        )}
      >
        {isBulkMove && (
          <div className={`${cssClass}__bulk-move-files`}>
            <Text
              tag="div"
              caption={i18n.t('modules.contentmanagement.movingFilesCount', { count: fileCount })}
              className={`${cssClass}__bulk-move-title`}
            />
            <div className={`${cssClass}__bulk-move-list`}>
              <Table
                noBorder
                data={movableFiles}
                columnMetadata={[
                  i18n.t('modules.contentmanagement.columnFileName'),
                  i18n.t('modules.contentmanagement.columnPath'),
                ]}
                cellRenderer={(_, file) => (
                  <Table.Row>
                    <Table.ColumnText caption={file.name} />
                    <Table.ColumnText caption={file.reference} />
                  </Table.Row>
                )}
              />
            </div>
          </div>
        )}
        {isStoreInitializing(store) ? (
          <div className={`${cssClass}__loader`}>
            <Loader size="lg" />
          </div>
        ) : (
          <FileManagerHierarchy
            data={store.data}
            skipActive
            label="modules.contentmanagement.folder"
            isLoading={store.isLoading}
            resolveIcon={store.resolveIcon}
            onHierarchyRowExpanding={handleRowExpanding}
            onHierarchyFolderHighlight={handleHighlightFolder}
          />
        )}
      </Modal>
    );
  }
);
